import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc, getDoc, getDocs, collection,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { FiArrowLeft, FiLoader } from "react-icons/fi";
import ClassDashboard from "./ClassDashboard";
import { useAuth } from "../../contexts/AuthContext";
import { backfillRosterDisplayForClass, fetchUsersMap } from "../../firebase/roster";

export default function TeacherClassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]); // [{id, display, enrollment{status,enrolledAt}}]
  const [teachers, setTeachers] = useState([]); // [{id, display, assignment{role,assignedAt}}]
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAllowed, setNotAllowed] = useState(false);
  const [error, setError] = useState("");

  const roles = Array.isArray(user?.roles) ? user.roles : (user?.role ? [user.role] : []);
  const isAdminOrEditor = roles.includes("admin") || roles.includes("editor");

  useEffect(() => {
    if (!id || !user?.uid) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      setNotAllowed(false);

      try {
        // 0) Kiểm tra quyền: admin/editor thì skip, còn lại phải là GV của lớp
        if (!isAdminOrEditor) {
          const myAssignRef = doc(db, "classes", id, "teachers", user.uid);
          const myAssignSnap = await getDoc(myAssignRef);
          if (!myAssignSnap.exists()) {
            if (!cancelled) setNotAllowed(true);
            return;
          }
        }

        // 1) Lấy lớp
        const classRef = doc(db, "classes", id);
        const classSnap = await getDoc(classRef);
        if (!classSnap.exists()) {
          if (!cancelled) setClassInfo(null);
          return;
        }
        const classData = { id: classSnap.id, ...classSnap.data() };
        if (cancelled) return;
        setClassInfo(classData);

        // 2) Lấy enrollments + teachers + programs song song
        const [enrollmentSnap, teacherSnap, progSnap] = await Promise.all([
          getDocs(collection(db, "classes", id, "enrollments")),
          getDocs(collection(db, "classes", id, "teachers")),
          getDocs(collection(db, "programs")),
        ]);
        if (cancelled) return;

        const enrollMeta = enrollmentSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
        const teacherMeta = teacherSnap.docs.map(d => ({ uid: d.id, ...d.data() }));

        // 3) Fallback: id nào thiếu display thì batch fetch users để hiển thị ngay
        const missingIds = [
          ...enrollMeta.filter(x => !x.display).map(x => x.uid),
          ...teacherMeta.filter(x => !x.display).map(x => x.uid),
        ];
        let userMap = {};
        if (missingIds.length) {
          userMap = await fetchUsersMap(missingIds);
        }

        const safeDisplay = (u) => {
          if (!u) return { name: "Unknown", email: "", avatarUrl: "" };
          const name = u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown";
          return { name, email: u.email || "", avatarUrl: u.avatarUrl || u.photoURL || "" };
        };

        // 4) Build view models
        const studentsArr = enrollMeta.map(en => ({
          id: en.uid,
          display: en.display || safeDisplay(userMap[en.uid]),
          enrollment: {
            status: en.status || "active",
            enrolledAt: en.enrolledAt || null,
          },
        }));
        const teachersArr = teacherMeta.map(tm => ({
          id: tm.uid,
          display: tm.display || safeDisplay(userMap[tm.uid]),
          assignment: {
            role: tm.role || "assistant",
            assignedAt: tm.assignedAt || null,
          },
        }));

        // 5) Programs
        const progs = progSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (cancelled) return;
        setStudents(studentsArr);
        setTeachers(teachersArr);
        setPrograms(progs);

        // 6) (tùy chọn) backfill dữ liệu cũ chưa có display — chỉ cho admin/editor
        if (isAdminOrEditor && missingIds.length) {
          // best-effort, không chặn UI
          backfillRosterDisplayForClass(id).catch(() => {});
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e?.message || "Đã có lỗi xảy ra khi tải dữ liệu.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.uid]);

  // --- Loading Skeleton ---
  if (loading) {
    return (
      <div className="max-w-7xl w-full mx-auto p-2 sm:p-4 flex flex-col items-center justify-center min-h-[320px]">
        <FiLoader className="animate-spin text-3xl text-primary mb-2" />
        <div className="text-text2 text-base font-medium">Đang tải chi tiết lớp...</div>
        <div className="w-full max-w-7xl mt-6 bg-card/80 rounded-2xl shadow-md border border-border h-[200px] animate-pulse" />
      </div>
    );
  }

  // --- Not allowed ---
  if (notAllowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[320px]">
        <div className="text-error text-lg mb-3 font-semibold">Bạn không có quyền xem lớp này.</div>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-primary/10 hover:bg-primary/20 text-primary font-semibold shadow"
          onClick={() => navigate(-1)}
        >
          <FiArrowLeft className="text-lg" /> Quay lại
        </button>
      </div>
    );
  }

  // --- Not found ---
  if (!classInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[320px]">
        <div className="text-error text-lg mb-3 font-semibold">Không tìm thấy lớp học này.</div>
        <button
          className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-primary/10 hover:bg-primary/20 text-primary font-semibold shadow"
          onClick={() => navigate(-1)}
        >
          <FiArrowLeft className="text-lg" /> Quay lại
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-2 sm:p-4">
      {/* Back button */}
      <button
        className="flex items-center gap-2 text-primary hover:underline font-medium mb-4"
        onClick={() => navigate(-1)}
      >
        <FiArrowLeft /> <span>Quay lại danh sách lớp</span>
      </button>

      {error && <div className="mb-3 text-error text-sm">{error}</div>}

      <ClassDashboard
        classInfo={classInfo}
        students={students}
        teachers={teachers}
        programs={programs}
        user={user}
        onRefresh={() => {
          // cho phép dashboard yêu cầu reload khi có action
          // (ví dụ đổi trạng thái HV, thêm GV...)
          // tái sử dụng useEffect phía trên
          const uid = user?.uid;
          if (!id || !uid) return;
          // gọi lại logic fetch bằng cách cập nhật state loading
          setLoading(true);
          // tái gọi logic chính
          (async () => {
            try {
              // chỉ gọi lại phần dữ liệu động nhanh (subcollections)
              const [enrollmentSnap, teacherSnap] = await Promise.all([
                getDocs(collection(db, "classes", id, "enrollments")),
                getDocs(collection(db, "classes", id, "teachers")),
              ]);
              const enrollMeta = enrollmentSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
              const teacherMeta = teacherSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
              const missingIds = [
                ...enrollMeta.filter(x => !x.display).map(x => x.uid),
                ...teacherMeta.filter(x => !x.display).map(x => x.uid),
              ];
              let userMap = {};
              if (missingIds.length) userMap = await fetchUsersMap(missingIds);
              const safeDisplay = (u) => {
                if (!u) return { name: "Unknown", email: "", avatarUrl: "" };
                const name = u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Unknown";
                return { name, email: u.email || "", avatarUrl: u.avatarUrl || u.photoURL || "" };
              };
              setStudents(enrollMeta.map(en => ({
                id: en.uid,
                display: en.display || safeDisplay(userMap[en.uid]),
                enrollment: { status: en.status || "active", enrolledAt: en.enrolledAt || null },
              })));
              setTeachers(teacherMeta.map(tm => ({
                id: tm.uid,
                display: tm.display || safeDisplay(userMap[tm.uid]),
                assignment: { role: tm.role || "assistant", assignedAt: tm.assignedAt || null },
              })));
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </div>
  );
}
